import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Award, ArrowRight } from "lucide-react";
import { ABOUT, IMAGES, SITE } from "@/data/content";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

const PageHeader = ({ overline, title, subtitle }) => (
  <section className="pt-16 pb-10 lg:pt-24 lg:pb-14 bg-white border-b border-[#6B8F71]/10">
    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
      <p className="text-sm font-medium tracking-[0.2em] uppercase text-[#6B8F71] mb-4">{overline}</p>
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#355C4A]">{title}</h1>
      {subtitle && <p className="mt-5 text-lg font-light text-[#5A655F] max-w-2xl mx-auto">{subtitle}</p>}
    </div>
  </section>
);

const About = () => {
  const { intro, philosophy, workWith, approach, qualifications } = ABOUT;

  return (
    <div data-testid="about-page">
      <PageHeader overline="About Us" title="Meet Harish B S" subtitle="Counsellor · Emotional Wellbeing · Personal Growth" />

      {/* Intro + portrait */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-5 gap-14 items-start">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={fade} className="lg:col-span-2">
            <div className="relative sticky top-28">
              <div className="overflow-hidden rounded-[2rem] shadow-[0_8px_40px_rgba(107,143,113,0.18)]">
                <img src={IMAGES.portrait} alt={SITE.counsellor} className="w-full h-[460px] object-cover" />
              </div>
              <div className="mt-6 bg-white rounded-2xl p-6 border border-[#6B8F71]/10 shadow-[0_8px_30px_rgba(107,143,113,0.06)]">
                <p className="font-serif text-2xl text-[#355C4A]">{SITE.counsellor}</p>
                <p className="text-sm text-[#6B8F71] tracking-wide">Counsellor, New Beginning</p>
              </div>
            </div>
          </motion.div>

          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} variants={fade} className="lg:col-span-3">
            <div className="space-y-5">
              {intro.body.map((p, i) => (
                <p key={i} className="text-base lg:text-lg font-light leading-relaxed text-[#2F2F2F]/85">{p}</p>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="bg-[#355C4A] py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <p className="text-sm font-medium tracking-[0.2em] uppercase text-[#C9A96A] mb-5">{philosophy.overline}</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-white">{philosophy.title}</h2>
          <div className="mt-10 space-y-2.5">
            {philosophy.lines.map((l, i) => (
              <p key={i} className="text-lg font-light text-[#DCE6DD]">{l}</p>
            ))}
          </div>
          <p className="mt-8 text-[#C7D4C9] font-light text-lg max-w-2xl mx-auto">{philosophy.close}</p>
        </div>
      </section>

      {/* Who I work with */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-2xl">
            <p className="text-sm font-medium tracking-[0.2em] uppercase text-[#6B8F71] mb-5">{workWith.overline}</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight text-[#355C4A]">{workWith.title}</h2>
            <p className="mt-5 text-lg font-light text-[#2F2F2F]/85">{workWith.intro}</p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {workWith.items.map((item, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.3 }}
                variants={fade}
                className="flex items-center gap-3 bg-white rounded-xl px-5 py-4 border border-[#6B8F71]/10"
              >
                <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[#6B8F71]/12 shrink-0">
                  <Check strokeWidth={2} className="w-3.5 h-3.5 text-[#355C4A]" />
                </span>
                <span className="text-[#2F2F2F] font-light">{item}</span>
              </motion.div>
            ))}
          </div>
          <p className="mt-8 text-[#5A655F] font-light text-lg max-w-3xl">{workWith.close}</p>
        </div>
      </section>

      {/* Approach + Qualifications */}
      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-14 lg:gap-20">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={fade}>
            <p className="text-sm font-medium tracking-[0.2em] uppercase text-[#6B8F71] mb-5">{approach.overline}</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-normal tracking-tight text-[#355C4A]">{approach.title}</h2>
            <div className="mt-5 space-y-4">
              {approach.body.map((p, i) => (
                <p key={i} className="text-base lg:text-lg font-light leading-relaxed text-[#2F2F2F]/85">{p}</p>
              ))}
            </div>
            <ul className="mt-6 grid sm:grid-cols-2 gap-3">
              {approach.methods.map((m, i) => (
                <li key={i} className="flex items-center gap-3 text-[#2F2F2F] font-light">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C9A96A]" /> {m}
                </li>
              ))}
            </ul>
            <p className="mt-6 text-[#5A655F] font-light italic">{approach.close}</p>
          </motion.div>

          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={fade}>
            <p className="text-sm font-medium tracking-[0.2em] uppercase text-[#6B8F71] mb-5">{qualifications.overline}</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-normal tracking-tight text-[#355C4A]">{qualifications.title}</h2>
            <div className="mt-6 space-y-4">
              {qualifications.items.map((q, i) => (
                <div key={i} className="flex items-start gap-4 bg-[#FAF8F4] rounded-xl p-5 border border-[#6B8F71]/10">
                  <Award strokeWidth={1.5} className="w-6 h-6 text-[#C9A96A] shrink-0 mt-0.5" />
                  <span className="text-[#2F2F2F] font-light leading-relaxed">{q}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl font-light text-[#355C4A]">Ready when you are</h2>
          <Link
            to="/contact"
            data-testid="about-cta-btn"
            className="mt-6 inline-flex items-center gap-2 bg-[#6B8F71] text-white px-8 py-4 rounded-full hover:bg-[#355C4A] hover:-translate-y-0.5 transition-all duration-300 font-medium"
          >
            Book Your First Session <ArrowRight strokeWidth={1.75} className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
